import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as classroomActions from '../../actions/classroom/classroomActions';
import * as classroomGroupActions from '../../actions/classroom/classroomGroupActions';
import * as alertService from '../../actions/messageAlertAction';
import { ROLE } from '../../const';
import { getStudentsWithId } from './AssignmentsContentPanel/assignmentsContentPanelService';
import * as drawerActions from '../../actions/drawerActions';
import { PropTypes } from 'prop-types';
import MultipleSelectionPanel from './MultipleSelectionPanel';
import { getAvailableGroupsAndUsers, getCurrentConfiguration } from '../../utils/AssignContentPanelUtils';
import * as customTranslate from '../../services/translate/customTranslateService';
import * as bookActions from '../../actions/bookActions';
import { DrawerCommonPanelCustom } from '../classroom/students/AddUsers/AddUserComponents';
import { I18n } from 'react-redux-i18n-lite';
import PanelChoosingCarousel from './CommonComponents/PanelChoosingCarousel';
import { ANALYTICS_CONTEXT } from '../../types/analytics';
import { BottomSheet, Button } from '@netexknowledge/smart-components';
import AssignGroupGrid from './AssignSheet/AssignGroupGrid';
import AssignStudentsList from './AssignSheet/AssignStudentsList';

class AssignContentPanel extends React.Component {
  state = {
    chooseMultiple: false,
    currentSelection: {}
  };

  componentDidMount = () => {
    const { classroom, classroomActions, classroomGroupActions, selectedResource, students } = this.props;

    classroomGroupActions.getClassroomGroups(classroom.id);
    classroomActions.getClassroomParticipants(classroom.id);

    const currentSelection = getCurrentConfiguration(selectedResource.configuration, students);

    this.setState({
      currentSelection
    });
  };

  assignContent = () => {
    const { bookActions, book, classroom, selectedResource } = this.props;
    const { currentSelection } = this.state;

    let context = ANALYTICS_CONTEXT.BOOK_ASSIGN;
    if (selectedResource.isUnit) {
      context = ANALYTICS_CONTEXT.UNIT_ASSIGN;
    } else if (selectedResource.isLesson) {
      context = ANALYTICS_CONTEXT.LESSON_ASSIGN;
    }

    bookActions
      .assignBookLevel(
        selectedResource.configuration,
        currentSelection,
        selectedResource.activityId,
        book.id,
        classroom.id,
        context
      )
      .then(() => {
        this.showMessageInfo(currentSelection);
        this.closePanel();
        bookActions.setSelectedResource();
      })
      .catch(() => {
        this.showMessageError(I18n.t('rightPanel.errorMessage'));
      });
  };

  showMessageInfo(currentSelection) {
    const { selectedResource, alertService, groups, language } = this.props;
    let resourceName = customTranslate.translateObject(selectedResource.name, language);
    let resourceText = selectedResource.hasOwnProperty('isLesson')
      ? I18n.t('assignMenu.success.lessonAssigned')
      : I18n.t('assignMenu.success.unitAssigned');
    if (currentSelection.isGroup) {
      const groupNameMapping = {
        highPerformance: I18n.t('groups.highPerformance'),
        reinforcement: I18n.t('groups.reinforcement')
      };

      const group = (groups || []).find(item => item.id === currentSelection.id);
      let groupName;
      if (group) {
        groupName = groupNameMapping[group.name] || group.name;
      } else if (currentSelection.isEveryone) {
        groupName = I18n.t('rightPanel.groups.everyone');
      }
      if (groupName) {
        alertService.info(
          `${resourceText} "${resourceName}" ${I18n.t('assignMenu.success.assignedPart')} "${groupName}"`
        );
      }
    } else {
      alertService.info(I18n.t('assignMenu.success.assignedMultiple'));
    }
  }

  showMessageError() {
    const { alertService } = this.props;
    alertService.error();
  }

  selectGroup = newSelection => {
    if (newSelection === 0) {
      this.setState({
        currentSelection: { id: newSelection, isGroup: true, isEveryone: true }
      });
    } else {
      this.setState({
        currentSelection: { id: newSelection, isGroup: true }
      });
    }
  };

  selectStudent = newSelection => {
    const { currentSelection } = this.state;
    const users = currentSelection.users.filter(user => user._id !== newSelection._id);

    this.setState({
      currentSelection: {
        users: users.length === currentSelection.users.length ? currentSelection.users.concat(newSelection) : users,
        isGroup: false
      }
    });
  };

  closePanel = () => this.props.drawerActions.closeDrawerAndRemoveContent();

  openChooseMultiple = () => {
    const { selectedResource, students } = this.props;
    const currentSelection = getCurrentConfiguration(selectedResource.configuration, students);
    this.setState({ chooseMultiple: true, currentSelection });
  };

  backToGroups = () => {
    const { selectedResource, students } = this.props;
    const currentSelection = getCurrentConfiguration(selectedResource.configuration, students);

    this.setState({ chooseMultiple: false, currentSelection });
  };

  isMultipleSelectionUnchanged = () => {
    const { currentSelection } = this.state;
    const { selectedResource } = this.props;
    return (
      typeof currentSelection.users === typeof selectedResource.configuration.users &&
      getStudentsWithId(currentSelection)
        .id.sort()
        .join() === selectedResource.configuration.users.sort().join()
    );
  };

  isGroupSelectionUnchanged = () => {
    const { currentSelection } = this.state;
    const { selectedResource } = this.props;
    return currentSelection.isGroup
      ? currentSelection.isEveryone
        ? selectedResource.configuration.groups.length === 0 && selectedResource.configuration.users.length === 0
        : currentSelection.id === selectedResource.configuration.groups[0]
      : this.isMultipleSelectionUnchanged();
  };

  getResourceName = () => {
    const { selectedResource, language } = this.props;
    return customTranslate.translateObject(selectedResource.name, language);
  };

  renderSheetFooter = (hideRowButtons, secondaryAction, primaryText) =>
    hideRowButtons ? (
      undefined
    ) : (
      <>
        <Button label={I18n.t('common.back')} onClick={secondaryAction} />
        <Button primary label={I18n.t(primaryText)} onClick={this.assignContent} />
      </>
    );

  // Mobile (Vista Mobile 3c): same state and actions as the drawer, laid out in a bottom sheet
  renderMultipleSelectionSheet = students => {
    const { currentSelection } = this.state;
    const selectedCount = (currentSelection.users || []).length;

    return (
      <BottomSheet
        open
        fullHeight
        onClose={this.closePanel}
        closeLabel={I18n.t('common.close')}
        onBack={this.backToGroups}
        backLabel={I18n.t('common.back')}
        title={I18n.t('panels.assignment.newAssignment')}
        subtitle={I18n.t('mobileSheets.multipleSelectionSelected', { count: selectedCount })}
        footer={this.renderSheetFooter(this.isMultipleSelectionUnchanged(), this.backToGroups, 'rightPanel.assign')}
      >
        <AssignStudentsList
          students={students}
          selectedUsers={currentSelection.users}
          selectStudent={this.selectStudent}
        />
      </BottomSheet>
    );
  };

  // Mobile (Vista Mobile 3a, 3b, 3d)
  renderGroupSelectionSheet = groups => {
    const { currentSelection } = this.state;

    return (
      <BottomSheet
        open
        fullHeight
        onClose={this.closePanel}
        closeLabel={I18n.t('common.close')}
        closePlacement="start"
        title={I18n.t('rightPanel.groups.assignTitle')}
        subtitle={this.getResourceName()}
        footer={this.renderSheetFooter(this.isGroupSelectionUnchanged(), this.closePanel, 'common.add')}
      >
        <AssignGroupGrid
          label={I18n.t('panels.assignment.whoAssignedResource')}
          groups={groups}
          currentSelection={currentSelection}
          selectGroup={this.selectGroup}
          openChooseMultiple={this.openChooseMultiple}
        />
      </BottomSheet>
    );
  };

  renderMultipleSelection = students => {
    const { currentSelection } = this.state;

    const hideRowButtons = this.isMultipleSelectionUnchanged();

    return (
      <DrawerCommonPanelCustom
        closePanel={this.backToGroups}
        hideRowButtons={hideRowButtons}
        panelHeaderTitle={I18n.t('panels.assignment.newAssignment')}
        showCancelButton={true}
        handleSecondaryButtonClick={this.backToGroups}
        primaryButtonText={'rightPanel.assign'}
        secondaryButtonText={'common.back'}
        icon={'left-chevron'}
        onPrimaryButtonClick={this.assignContent}
      >
        <MultipleSelectionPanel
          students={students}
          selectStudent={this.selectStudent}
          currentSelection={currentSelection.users}
        />
      </DrawerCommonPanelCustom>
    );
  };

  renderLessonGroupSelection = groups => {
    const { currentSelection } = this.state;
    const { students } = this.props;

    const hideRowButtons = this.isGroupSelectionUnchanged();

    return (
      <DrawerCommonPanelCustom
        closePanel={this.closePanel}
        hideRowButtons={hideRowButtons}
        panelHeaderTitle={I18n.t('rightPanel.groups.assignTitle')}
        showCancelButton={true}
        handleSecondaryButtonClick={this.closePanel}
        primaryButtonText={'common.add'}
        secondaryButtonText={'common.back'}
        onPrimaryButtonClick={this.assignContent}
      >
        <PanelChoosingCarousel
          label={I18n.t('panels.assignment.whoAssignedResource')}
          groups={groups}
          setSelectedGroup={this.selectGroup}
          mode={'create'}
          selectedGroup={currentSelection}
          openChooseMultiple={this.openChooseMultiple}
          students={students}
        />
      </DrawerCommonPanelCustom>
    );
  };

  render() {
    const { chooseMultiple } = this.state;
    const { students, groups, selectedResource, variant } = this.props;

    const { availableStudents, availableGroups } = getAvailableGroupsAndUsers(students, groups, selectedResource);

    if (variant === 'sheet') {
      return chooseMultiple
        ? this.renderMultipleSelectionSheet(availableStudents)
        : this.renderGroupSelectionSheet(availableGroups);
    }

    return chooseMultiple
      ? this.renderMultipleSelection(availableStudents)
      : this.renderLessonGroupSelection(availableGroups);
  }
}

AssignContentPanel.propTypes = {
  classroom: PropTypes.any,
  classroomActions: PropTypes.any,
  classroomGroupActions: PropTypes.any,
  drawerActions: PropTypes.any,
  selectedResource: PropTypes.any,
  students: PropTypes.any,
  groups: PropTypes.any,
  books: PropTypes.any,
  book: PropTypes.any,
  backgroundSecondary: PropTypes.any,
  alertService: PropTypes.any,
  bookActions: PropTypes.any,
  language: PropTypes.any,
  // 'sheet': mobile bottom sheet instead of the right drawer layout
  variant: PropTypes.oneOf(['drawer', 'sheet'])
};

function mapStateToProps(state) {
  return {
    groups: state.classrooms.selectedClassroom.groups,
    classroom: state.classrooms.selectedClassroom.classroom,
    students: state.classrooms.selectedClassroom.participants.filter(participant => participant.role === ROLE.STUDENT),
    selectedResource: state.book.selectedResource,
    i18n: state.i18n,
    language: state.account.user.language,
    backgroundSecondary: state.theme.colors.backgroundSecondary,
    books: state.book.books,
    book: state.book.book
  };
}

function mapDispatchToProps(dispatch) {
  return {
    alertService: bindActionCreators(alertService, dispatch),
    classroomActions: bindActionCreators(classroomActions, dispatch),
    classroomGroupActions: bindActionCreators(classroomGroupActions, dispatch),
    drawerActions: bindActionCreators(drawerActions, dispatch),
    bookActions: bindActionCreators(bookActions, dispatch),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AssignContentPanel);
